// "Thinking…" indicator for long AI searches. The search runs in a Web
// Worker (search-client.ts), so the UI never freezes — this is purely an
// activity hint for when a single search takes a while.
//
// Trigger is PER SEARCH, not per turn: the banner appears only once an
// individual search has been running longer than `config.aiThinkingDelayMs`.
// An AI turn is several searches separated by animation gaps; measuring the
// delay across the whole turn (gaps included) made the banner flash up on
// turns that weren't actually slow. Per-search delay only fires on a genuinely
// long computation — the real "the AI is hanging" case.
//
// Once shown it stays up for at least MIN_VISIBLE_MS (so it never flashes) and
// bridges the gaps between back-to-back searches in a turn (HIDE_DEBOUNCE_MS)
// so it doesn't flicker. The bar is indeterminate — total work across a turn
// is unknowable, so a real fraction would just fill/drain/refill per search.

import { getConfig } from '../config.js';

// Keep the banner up at least this long once shown → no flash.
const MIN_VISIBLE_MS = 1000;
// Bridge the gap between consecutive searches in one AI turn (play animation +
// aiTurnDelay) so the banner doesn't flicker off between the AI's actions.
const HIDE_DEBOUNCE_MS = 1200;

let overlay: HTMLElement | null = null;
let active = 0;             // searches currently running
let shown = false;
let shownAt = 0;
let showTimer: ReturnType<typeof setTimeout> | null = null;
let hideTimer: ReturnType<typeof setTimeout> | null = null;

function ensureOverlay(): HTMLElement {
  if (overlay) return overlay;
  overlay = document.createElement('div');
  overlay.className = 'ai-thinking';
  overlay.setAttribute('role', 'status');
  overlay.setAttribute('aria-live', 'polite');
  overlay.innerHTML = `
    <span class="ai-thinking-label">Thinking…</span>
    <span class="ai-thinking-bar"><span class="ai-thinking-fill"></span></span>
  `;
  document.body.appendChild(overlay);
  return overlay;
}

function clearTimer(t: ReturnType<typeof setTimeout> | null): null {
  if (t !== null) clearTimeout(t);
  return null;
}

function show(): void {
  ensureOverlay().classList.add('is-visible');
  shown = true;
  shownAt = performance.now();
}

function doHide(): void {
  overlay?.classList.remove('is-visible');
  shown = false;
}

/** Call when an AI search begins. */
export function thinkingStart(): void {
  active++;
  hideTimer = clearTimer(hideTimer);
  // Per-search show delay: (re)arm only while nothing is shown yet. Restarting
  // it each search means the clock is per individual search, not cumulative
  // across the turn's gaps.
  if (!shown && showTimer === null) {
    showTimer = setTimeout(() => {
      showTimer = null;
      if (active > 0) show();
    }, getConfig().aiThinkingDelayMs);
  }
}

/** Call when an AI search resolves (in a finally). */
export function thinkingEnd(): void {
  active = Math.max(0, active - 1);
  if (active > 0) return;

  // No search running right now. A still-pending show timer means this search
  // finished before the delay — cancel it so a between-search gap can't show
  // the banner for a search that already returned.
  showTimer = clearTimer(showTimer);

  if (!shown) return;

  // Shown: schedule a hide that (a) respects the minimum visible time and
  // (b) debounces across the gap to the next search in this turn.
  const elapsed = performance.now() - shownAt;
  const wait = Math.max(HIDE_DEBOUNCE_MS, MIN_VISIBLE_MS - elapsed);
  hideTimer = clearTimer(hideTimer);
  hideTimer = setTimeout(() => {
    hideTimer = null;
    if (active === 0) doHide();
  }, wait);
}

/** Force everything off (e.g. game over / leaving the match). */
export function thinkingReset(): void {
  active = 0;
  showTimer = clearTimer(showTimer);
  hideTimer = clearTimer(hideTimer);
  doHide();
}
